import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Section, Heading, Spinner } from "../components/Bits";
import PageHero from "../components/PageHero";
import LoginGate from "../components/LoginGate";
import { ChefHat, Plus, X, Sparkles } from "lucide-react";

const STYLES = [
  { value: "einfach", label: "Einfach & schnell" },
  { value: "rustikal", label: "Rustikal & traditionell" },
  { value: "modern", label: "Modern & raffiniert" },
  { value: "vegan", label: "Vegan" },
  { value: "salat", label: "Salat / Rohkost" },
  { value: "suppe", label: "Suppe" },
  { value: "pesto", label: "Pesto / Aufstrich" },
  { value: "getraenk", label: "Getränk / Sirup" },
];

// Lightweight markdown renderer (headings, lists, bold, paragraphs)
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const blocks = [];
  let listBuf = [];
  const flushList = (key) => {
    if (listBuf.length) {
      blocks.push(
        <ul key={`ul-${key}`} className="list-disc pl-6 space-y-1 my-3">
          {listBuf.map((l, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: inline(l) }} />
          ))}
        </ul>
      );
      listBuf = [];
    }
  };
  const inline = (s) =>
    s
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>");

  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (/^###\s+/.test(line)) {
      flushList(idx);
      blocks.push(<h3 key={idx} className="font-display text-xl mt-5 mb-2">{line.replace(/^###\s+/, "")}</h3>);
    } else if (/^##\s+/.test(line)) {
      flushList(idx);
      blocks.push(<h2 key={idx} className="font-display text-2xl mt-6 mb-2">{line.replace(/^##\s+/, "")}</h2>);
    } else if (/^#\s+/.test(line)) {
      flushList(idx);
      blocks.push(<h1 key={idx} className="font-display text-3xl mt-6 mb-3">{line.replace(/^#\s+/, "")}</h1>);
    } else if (/^[-*]\s+/.test(line)) {
      listBuf.push(line.replace(/^[-*]\s+/, ""));
    } else if (/^\d+\.\s+/.test(line)) {
      listBuf.push(line.replace(/^\d+\.\s+/, ""));
    } else if (line.trim() === "") {
      flushList(idx);
    } else {
      flushList(idx);
      blocks.push(<p key={idx} className="my-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(line) }} />);
    }
  });
  flushList("end");
  return blocks;
}

export default function Recipes() {
  const { user } = useAuth() || {};
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]); // list of plant names
  const [style, setStyle] = useState("einfach");
  const [gen, setGen] = useState(false);
  const [recipe, setRecipe] = useState("");

  useEffect(() => {
    api.get("/plants").then((r) => {
      // Edible/medicinal + Bäume/Sträucher (Früchte/Blätter oft essbar)
      const safe = (r.data || []).filter((p) => ["edible", "medicinal", "tree", "shrub"].includes(p.category));
      setPlants(safe);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return plants.slice(0, 30);
    return plants.filter((p) =>
      p.name_de.toLowerCase().includes(q) || (p.name_lat || "").toLowerCase().includes(q)
    ).slice(0, 30);
  }, [plants, query]);

  const add = (name) => {
    if (selected.includes(name)) return;
    if (selected.length >= 5) {
      toast.warning("Maximal 5 Pflanzen pro Rezept");
      return;
    }
    setSelected([...selected, name]);
  };

  const remove = (name) => setSelected(selected.filter((n) => n !== name));

  const generate = async () => {
    if (selected.length === 0) {
      toast.error("Bitte mindestens eine Pflanze auswählen");
      return;
    }
    setGen(true);
    setRecipe("");
    try {
      const r = await api.post("/recipes/generate", { plant_names: selected, style });
      setRecipe(r.data.recipe || "");
    } catch (e) {
      toast.error("Rezeptgenerierung fehlgeschlagen: " + (e?.response?.data?.detail || e.message));
    } finally {
      setGen(false);
    }
  };

  if (loading) return <Section><Spinner /></Section>;
  if (!user) return <LoginGate icon={ChefHat} title="KI-Rezept-Generator" subtitle="Claude schreibt dir gleich ein Wildkräuter-Rezept – bitte kurz einloggen." />;

  return (
    <Section>
      <PageHero slug="recipes" variant="soft"
        defaultOverline="KI-Rezept-Generator"
        defaultTitle="Wilde Küche – mit KI"
        defaultSubtitle="Wähle bis zu 5 Wildpflanzen aus unserer Datenbank, dazu einen Stil – Claude Sonnet 4.5 erstellt dir ein vollständiges, sicheres Rezept."
      />

      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8">
        {/* Selector */}
        <div className="plant-card p-6 space-y-5" data-testid="recipe-selector">
          <div>
            <label className="overline">Suche</label>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="z. B. Brennnessel, Giersch, Bärlauch …"
              className="w-full mt-1 px-4 py-3 rounded-xl border bg-white"
              style={{ borderColor: "var(--wp-border)" }}
              data-testid="recipe-search-input"
            />
          </div>

          {selected.length > 0 && (
            <div>
              <div className="overline mb-2">Ausgewählt ({selected.length}/5)</div>
              <div className="flex flex-wrap gap-2" data-testid="recipe-selected-chips">
                {selected.map((n) => (
                  <button
                    key={n}
                    onClick={() => remove(n)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border"
                    style={{ backgroundColor: "var(--wp-cream)", borderColor: "var(--wp-border)" }}
                    data-testid={`recipe-chip-${n}`}
                  >
                    {n} <X className="w-3.5 h-3.5 opacity-70" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="overline">Stil</label>
            <select
              value={style}
              onChange={(e) => setStyle(e.target.value)}
              className="w-full mt-1 px-4 py-3 rounded-xl border bg-white"
              style={{ borderColor: "var(--wp-border)" }}
              data-testid="recipe-style-select"
            >
              {STYLES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          <button
            onClick={generate}
            disabled={gen || selected.length === 0}
            className="btn-primary w-full justify-center"
            data-testid="recipe-generate-btn"
          >
            <Sparkles className="w-4 h-4" /> {gen ? "Erstelle Rezept …" : "Rezept generieren"}
          </button>

          <div>
            <div className="overline mb-2">Vorschläge {query && <span className="opacity-60">– {filtered.length} Treffer</span>}</div>
            <div className="max-h-72 overflow-y-auto space-y-1 pr-1" data-testid="recipe-suggestions">
              {filtered.length === 0 && <div className="text-sm opacity-60 py-3">Nichts gefunden.</div>}
              {filtered.map((p) => {
                const isSel = selected.includes(p.name_de);
                return (
                  <button
                    key={p.id}
                    onClick={() => add(p.name_de)}
                    disabled={isSel}
                    className="w-full flex items-center gap-3 p-2 rounded-lg text-left hover:bg-[var(--wp-cream)] transition-colors disabled:opacity-50"
                    data-testid={`recipe-add-${p.slug}`}
                  >
                    <img src={p.image_url} alt={p.name_de} className="w-10 h-10 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{p.name_de}</div>
                      <div className="text-xs italic opacity-60 truncate">{p.name_lat}</div>
                    </div>
                    {!isSel && <Plus className="w-4 h-4 opacity-70" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recipe output */}
        <div className="plant-card p-8 min-h-[400px]" data-testid="recipe-output">
          <div className="flex items-center gap-2 mb-4">
            <ChefHat className="w-5 h-5" style={{ color: "var(--wp-primary)" }} />
            <h3 className="font-display text-2xl">Dein Rezept</h3>
          </div>
          {gen && <Spinner label="Claude Sonnet 4.5 kocht für dich …" />}
          {!gen && !recipe && (
            <div className="text-sm opacity-70 mt-6">
              Wähle links Wildpflanzen aus, entscheide dich für einen Stil und drücke «Rezept generieren". Das Ergebnis erscheint hier.
              <div className="mt-6 text-xs p-3 rounded-lg" style={{ backgroundColor: "rgba(167,58,58,0.06)" }}>
                ⚠ Bitte nur Pflanzen verwenden, die du sicher bestimmen kannst. Im Zweifel: KI-Erkennung nutzen oder geführte Tour buchen.
              </div>
            </div>
          )}
          {!gen && recipe && (
            <div className="prose-wp text-[var(--wp-text)]" data-testid="recipe-content">
              {renderMarkdown(recipe)}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}
